import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import ActionBar from '../../../../components/ui/ActionBar';
import {
    getAdvisoryGroupPotentialMembers,
    getAdvisoryGroupSelectedMembers,
    getMemberComments,
    resetPanelSelection,
} from '../../../../ducks/advisoryGroup';
import DataGrid from '../../../../components/DataGrid';
import { toDateRaw } from '../../../../utils/utils';
import Loader from '../../../../components/ui/Loader';
import Icon from '../../../../components/ui/Icon';
import PanelDistanceMilesColumnHeader from '../../../../components/ui/PanelDistanceMilesColumnHeader';
import messages from '../../../../utils/helper/messages';
import OutreachDetailsModal from '../recruiting-outreach/OutreachDetailsModal';
import styles from './PanelSelectionRecruitment.module.scss';
import { MEMBER_OUTCOME_STATUS_VALUE } from '../../../../constants';
import memberAnalyticsApi from '../../../../services/api/memberAnalyticsService';
import {
    enrichTextWithAgeQuartileRanges,
    formatPanelGapDisplayLabel,
    formatRankingContributionsForTooltip,
    getCohortAgeQuartileCutpoints,
    getCohortMemberFromListById,
    getPanelRankingBandLabel,
    getPanelRankingSupplementalDetail,
    getRankingBreakdownTopContributions,
    hasPanelProximityMeetingZip,
    isAugmentedPlaceholderMember,
    mergeCohortMemberIntoAgOutreachRow,
    panelRankingContextMentionsAgeQuartiles,
} from '../../../../utils/recruitmentAugmentedMember';

function buildPanelFitTooltipText(orig) {
    const narrative = getPanelRankingSupplementalDetail(orig);
    const driverBlock = formatRankingContributionsForTooltip(getRankingBreakdownTopContributions(orig));
    const baseGeneric = messages.info.panelCandidateRankingTooltip;
    const legend = messages.info.panelCandidateRankingBandsLegend;

    if (!narrative) {
        return driverBlock ? `${baseGeneric}\n\n${driverBlock}` : baseGeneric;
    }
    const parts = [narrative];
    if (driverBlock) parts.push(driverBlock);
    parts.push(legend);
    return parts.join('\n\n');
}

const getPanelCandidateRowClassName = (ranking) => {
    const r = Number(ranking);
    if (Number.isNaN(r)) return '';
    if (r >= 90 && r <= 100) return styles.rowRankGreen;
    if (r >= 75 && r < 90) return styles.rowRankYellow;
    if (r >= 50 && r < 75) return styles.rowRankOrange;
    if (r >= 0 && r < 50) return styles.rowRankRed;
    return '';
};

const PanelSelection = ({
    selectedAgId,
    cohortId = null,
    callDispositionData,
    onEdit,
    proximityVenueSelection = '',
    onProximityVenueSelectionChange,
}) => {
    const dispatch = useDispatch();
    const potentialMembers = useSelector((state) => state.advisoryGroup?.panelPotentialMembers);
    const panelRankingContext = useSelector((state) => state.advisoryGroup?.panelRankingContext);
    const panelProximityVenues = useSelector((state) => state.advisoryGroup?.panelProximityVenues);
    const panelProximityDefaultLocationId = useSelector(
        (state) => state.advisoryGroup?.panelProximityDefaultLocationId,
    );
    // Primitives only: Redux replaces `panelProximityVenues` with a new array each fetch; depending on
    // that array in useMemo would change `proximityQuery` by reference every time and retrigger the
    // panel-potential-members effect forever ("Updating distances and rankings…").
    const firstVenueLocationId =
        panelProximityVenues?.[0]?.locationId != null && String(panelProximityVenues[0].locationId).trim() !== ''
            ? String(panelProximityVenues[0].locationId).trim()
            : '';

    const [recruitModal, setRecruitModal] = useState(false);
    const [proximityRefetching, setProximityRefetching] = useState(false);
    const [outreachData, setOutreachData] = useState(null);
    const [cohortMembersList, setCohortMembersList] = useState(null);
    const [panelGapsOpen, setPanelGapsOpen] = useState(false);
    const panelGapsContentId = useId();

    useEffect(() => {
        if (!cohortId || !potentialMembers?.length) {
            setCohortMembersList(null);
            return;
        }
        const needsCohort = potentialMembers.some((row) => isAugmentedPlaceholderMember(row));
        if (!needsCohort) {
            setCohortMembersList(null);
            return;
        }
        let cancelled = false;
        memberAnalyticsApi
            .getMembersData(cohortId)
            .then((res) => {
                if (!cancelled) setCohortMembersList(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                if (!cancelled) setCohortMembersList([]);
            });
        return () => {
            cancelled = true;
        };
    }, [cohortId, potentialMembers]);

    const displayPotentialMembers = useMemo(() => {
        if (!potentialMembers?.length) return potentialMembers || [];
        if (!cohortMembersList?.length) {
            return potentialMembers.map((row) =>
                isAugmentedPlaceholderMember(row) ? { ...row, _augmentedCohortPending: true } : row
            );
        }
        return potentialMembers.map((row) => {
            if (!isAugmentedPlaceholderMember(row)) return row;
            const cm = getCohortMemberFromListById(cohortMembersList, row.memberId);
            return cm ? mergeCohortMemberIntoAgOutreachRow(row, cm) : { ...row, _augmentedCohortPending: true };
        });
    }, [potentialMembers, cohortMembersList]);

    const handleRecruit = (row) => {
        setRecruitModal(true);
        setOutreachData({
            ...row,
            comment: '',
            memberCommunicationLastDate: row.memberCommunicationLastDate ? toDateRaw(row.memberCommunicationLastDate) : null
        });
        dispatch(getMemberComments(row.id));
    }

    const handleRecruitModalClose = () => {
        setRecruitModal(false);
        setOutreachData(null);
    }

    const prevAgIdRef = useRef(null);
    const prevProximityQueryKeyRef = useRef(null);
    const panelPotentialFetchGenRef = useRef(0);

    const proximityQuery = useMemo(() => {
        const q = {};
        const loc =
            (proximityVenueSelection && String(proximityVenueSelection).trim())
            || panelProximityDefaultLocationId
            || firstVenueLocationId;
        if (loc != null && String(loc).trim() !== '') {
            q.locationId = String(loc).trim();
        }
        return q;
    }, [proximityVenueSelection, panelProximityDefaultLocationId, firstVenueLocationId]);

    const proximitySelectValue = useMemo(() => {
        if (proximityVenueSelection) {
            return proximityVenueSelection;
        }
        if (panelProximityDefaultLocationId) {
            return String(panelProximityDefaultLocationId);
        }
        return firstVenueLocationId;
    }, [proximityVenueSelection, panelProximityDefaultLocationId, firstVenueLocationId]);

    useEffect(() => {
        if (!selectedAgId || selectedAgId === 0) {
            prevAgIdRef.current = selectedAgId;
            prevProximityQueryKeyRef.current = null;
            setProximityRefetching(false);
            return;
        }
        const agChanged =
            prevAgIdRef.current == null || prevAgIdRef.current !== selectedAgId;
        prevAgIdRef.current = selectedAgId;

        const queryKey = JSON.stringify(proximityQuery);
        const proximityOnlyChanged =
            !agChanged
            && prevProximityQueryKeyRef.current != null
            && prevProximityQueryKeyRef.current !== queryKey;

        if (agChanged) {
            prevProximityQueryKeyRef.current = null;
            dispatch(resetPanelSelection());
            dispatch(getAdvisoryGroupSelectedMembers(selectedAgId));
        }

        if (proximityOnlyChanged) {
            setProximityRefetching(true);
        }

        prevProximityQueryKeyRef.current = queryKey;

        const gen = ++panelPotentialFetchGenRef.current;
        const pending = dispatch(getAdvisoryGroupPotentialMembers(selectedAgId, proximityQuery));
        if (pending != null && typeof pending.finally === 'function') {
            pending.finally(() => {
                if (gen === panelPotentialFetchGenRef.current) {
                    setProximityRefetching(false);
                }
            });
        } else {
            setProximityRefetching(false);
        }
    }, [selectedAgId, proximityQuery, dispatch]);

    useEffect(() => {
        setPanelGapsOpen(false);
    }, [selectedAgId]);

    const showProximityDistanceColumn = hasPanelProximityMeetingZip(panelRankingContext);

    const potentialMembersRowActions = ({ row }) => (
        <div className="text-center">
            <Button
                size="sm"
                type="button"
                onClick={() => handleRecruit(row.original)}
            >
                Recruit
            </Button>
        </div>
    );

    const potentialMembersColumns = [
        {
            id: 'firstName',
            header: 'Name',
            accessorKey: 'firstName',
            accessorFn: (row) =>
                row.firstName ? `${row.firstName} ${row.lastName}` : '',
            cell: ({ row }) =>
                row.original._augmentedCohortPending ? (
                    <span className="text-muted">…</span>
                ) : (
                    <>
                        {row.original.firstName} {row.original.lastName}
                    </>
                ),
            enableColumnFilter: false,
            maxSize: 150,
        },
        {
            id: 'primaryLanguage',
            header: 'Language',
            accessorKey: 'primaryLanguage',
            cell: ({ row }) => row.original.primaryLanguage ?? <span>&nbsp;</span>,
            maxSize: 150,
        },
        {
            id: 'panelFit',
            header: 'Panel fit',
            accessorKey: 'ranking',
            enableColumnFilter: false,
            cell: ({ row }) => {
                const orig = row.original;
                const r = Number(orig.ranking);
                const hasScore = Number.isFinite(r);
                const label = getPanelRankingBandLabel(orig.ranking);
                const tip = buildPanelFitTooltipText(orig);
                return (
                    <OverlayTrigger
                        placement="left"
                        overlay={
                            <Tooltip
                                style={{
                                    maxWidth: 420,
                                    whiteSpace: 'pre-wrap',
                                    textAlign: 'left',
                                }}>
                                {tip}
                            </Tooltip>
                        }>
                        <span className="cursor-pointer d-inline-flex flex-column align-items-start gap-0 text-start">
                            <span className="d-inline-flex align-items-center gap-1">
                                <span className="fw-semibold text-nowrap">{hasScore ? Math.round(r) : '—'}</span>
                                <Icon icon="info-solid" size={14} className="opacity-75 flex-shrink-0" />
                            </span>
                            {hasScore && label ? (
                                <span className="text-muted small text-break d-block">{label}</span>
                            ) : null}
                        </span>
                    </OverlayTrigger>
                );
            },
            minSize: 92,
            size: 104,
            maxSize: 120,
        },
        ...(showProximityDistanceColumn
            ? [
                  {
                      id: 'distanceMiles',
                      header: () =>
                          React.createElement(PanelDistanceMilesColumnHeader, {
                              panelRankingContext,
                          }),
                      accessorKey: 'distanceMiles',
                      accessorFn: (row) =>
                          row.distanceMiles != null && Number.isFinite(Number(row.distanceMiles))
                              ? Math.round(Number(row.distanceMiles))
                              : null,
                      filterFn: 'lteNumber',
                      meta: {
                          panelDistanceMaxFilter: true,
                          panelDistanceFilterMaxMiles: 75,
                          panelDistanceFilterStepMiles: 5,
                          filterItemFormat: (f) => {
                              if (f === 'None') return 'None';
                              if (typeof f === 'number' && Number.isFinite(f)) {
                                  return `≤ ${f} mi`;
                              }
                              if (f === null || f === undefined) {
                                  return '—';
                              }
                              return String(f);
                          },
                      },
                      cell: ({ row }) => {
                          const d = row.original.distanceMiles;
                          if (d == null || !Number.isFinite(Number(d))) {
                              return <span className="text-muted">—</span>;
                          }
                          return <span className="text-nowrap">{Math.round(Number(d))}</span>;
                      },
                      minSize: 128,
                      size: 144,
                      maxSize: 180,
                  },
              ]
            : []),
        {
            id: 'memberStatus',
            header: 'Status',
            accessorKey: 'memberStatus',
            cell: ({ row }) => row.original.memberStatus ?? <span>&nbsp;</span>,
        },
        {
            id: 'outcome',
            header: 'Outcome',
            accessorKey: 'outcome',
            accessorFn: (row) => `${MEMBER_OUTCOME_STATUS_VALUE[row.outcome] || ''}`,
            cell: ({ row }) =>
                row.original.outcome ? MEMBER_OUTCOME_STATUS_VALUE[row.original.outcome] : <span>&nbsp;</span>,
        },
        {
            id: 'callDisposition',
            header: 'Disposition',
            accessorKey: 'callDisposition',
            accessorFn: (row) => `${callDispositionData?.find(({ id }) => (id === row.callDisposition))?.value || ''}`,
            cell: ({ row }) =>
                row.original.callDisposition ? (
                    callDispositionData?.find(({ id }) => id === row.original.callDisposition)?.value
                ) : (
                    <span>&nbsp;</span>
                ),
        },
        {
            id: 'memberCommunicationLastDate',
            header: 'Last Outreach Date',
            accessorKey: 'memberCommunicationLastDate',
            accessorFn: (row) => `${row.memberCommunicationLastDate !== null ? (toDateRaw(row.memberCommunicationLastDate)) : ''}`,
            cell: ({ row }) =>
                row.original.memberCommunicationLastDate ? (
                    toDateRaw(row.original.memberCommunicationLastDate)
                ) : (
                    <span>&nbsp;</span>
                ),
            sortType: 'date',
            sortingFn: 'datetime',
            enableColumnFilter: false
        },
        {
            id: 'outreachAttempts',
            header: 'Attempts',
            accessorKey: 'outreachAttempts',
            cell: ({ row }) => row.original.outreachAttempts ?? <span>&nbsp;</span>,
            filterFn: 'equals'
        },
        {
            id: 'actions',
            header: '',
            cell: potentialMembersRowActions,
        },
    ];

    const showPanelGapsSection =
        panelRankingContext &&
        (panelRankingContext.panelAggregateSummary ||
            panelRankingContext.lowPanelSampleWarning ||
            (panelRankingContext.aggregateUnderrepresentation?.length > 0) ||
            panelRankingContext.methodologyNote);

    const cohortAgeCutpoints = panelRankingContext
        ? getCohortAgeQuartileCutpoints(panelRankingContext)
        : null;
    const showAgeQuartileExplainer =
        panelRankingContext &&
        panelRankingContextMentionsAgeQuartiles(panelRankingContext) &&
        !cohortAgeCutpoints;
    const panelAggregateSummaryDisplay =
        panelRankingContext?.panelAggregateSummary &&
        enrichTextWithAgeQuartileRanges(
            panelRankingContext.panelAggregateSummary,
            cohortAgeCutpoints
        );

    const showProximityVenuePicker =
        Array.isArray(panelProximityVenues) && panelProximityVenues.length > 0 && onProximityVenueSelectionChange;

    return (
        <div className="px-3">
            <ActionBar
                title="Advisory Group Candidates"
                titleClass="fw-bold"
            />
            {showProximityVenuePicker ? (
                <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                    <Form.Label htmlFor="panel-proximity-venue" className="mb-0 small fw-semibold text-nowrap">
                        Panel distance reference
                    </Form.Label>
                    <Form.Select
                        id="panel-proximity-venue"
                        size="sm"
                        className="flex-grow-1"
                        style={{ minWidth: 220, maxWidth: 560 }}
                        value={proximitySelectValue}
                        disabled={proximityRefetching}
                        aria-busy={proximityRefetching}
                        onChange={(e) => onProximityVenueSelectionChange(e.target.value)}>
                        {panelProximityVenues.map((v) => (
                            <option key={v.locationId} value={v.locationId}>
                                {v.label}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            ) : null}
            {showPanelGapsSection ? (
                <div className="mb-3 border rounded small bg-light overflow-hidden">
                    <button
                        type="button"
                        className="w-100 d-flex align-items-center justify-content-between py-2 px-3 border-0 bg-transparent text-start cursor-pointer user-select-none"
                        onClick={() => setPanelGapsOpen((o) => !o)}
                        aria-expanded={panelGapsOpen}
                        aria-controls={panelGapsContentId}>
                        <span className="fw-semibold">Panel gaps</span>
                        <Icon
                            icon={panelGapsOpen ? 'arrow-up' : 'arrow-down'}
                            size={14}
                            className="opacity-75 flex-shrink-0 ms-2"
                            aria-hidden
                        />
                    </button>
                    <Collapse in={panelGapsOpen}>
                        <div id={panelGapsContentId}>
                            <div className="border-top px-3 py-2 bg-white">
                                {panelAggregateSummaryDisplay ? (
                                    <p className="mb-2">{panelAggregateSummaryDisplay}</p>
                                ) : null}
                                {panelRankingContext.lowPanelSampleWarning ? (
                                    <p className="text-warning-emphasis small mb-2">
                                        {messages.info.panelGapsLowSampleWarning}
                                    </p>
                                ) : null}
                                {showAgeQuartileExplainer ? (
                                    <p className="text-muted small mb-2">
                                        {messages.info.panelGapsAgeQuartilesExplainer}
                                    </p>
                                ) : null}
                                {panelRankingContext.aggregateUnderrepresentation?.length > 0 ? (
                                    <ul className="mb-0 ps-3">
                                        {panelRankingContext.aggregateUnderrepresentation.map((gap, i) => {
                                            const gapLabel = formatPanelGapDisplayLabel(gap, panelRankingContext);
                                            return (
                                                <li key={i} className="mb-1">
                                                    {gapLabel ? <span className="fw-semibold">{gapLabel}</span> : null}
                                                    {gap.narrative ? (
                                                        <span>
                                                            {gapLabel ? ': ' : null}
                                                            {gap.narrative}
                                                        </span>
                                                    ) : null}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null}
                                {panelRankingContext.methodologyNote ? (
                                    <p className="text-muted mt-2 mb-0 small">
                                        {panelRankingContext.methodologyNote}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </Collapse>
                </div>
            ) : null}
            {selectedAgId !== 0 && potentialMembers === null ? (
                <Loader />
            ) : (
                <div className={`position-relative ${styles.panelCandidatesGridWrap}`}>
                    {proximityRefetching ? (
                        <div
                            className={styles.panelCandidatesGridOverlay}
                            aria-live="polite"
                            aria-label="Updating distances and rankings">
                            <Spinner animation="border" size="sm" className="me-2" role="status" />
                            <span className="small text-muted">Updating distances and rankings…</span>
                        </div>
                    ) : null}
                    <DataGrid
                        columns={potentialMembersColumns}
                        data={displayPotentialMembers}
                        pagination={false}
                        gridContainerClass={`${styles.panelCandidatesGrid} grid-with-scroll overflow-auto`}
                        stickyHeader
                        getRowClassName={(row) => getPanelCandidateRowClassName(row.original.ranking)}
                    />
                </div>
            )}

            {recruitModal && (
                <OutreachDetailsModal
                    show={recruitModal}
                    data={outreachData}
                    cohortId={cohortId}
                    info={{
                        id: outreachData?.id,
                        agId: selectedAgId,
                    }}
                    onEdit={onEdit}
                    onCancel={handleRecruitModalClose}
                />
            )}
        </div>
    );
};

export default PanelSelection;
